// Image preloader utility for better performance
class ImagePreloader {
  constructor() {
    this.cache = new Map();
    this.loading = new Set();
  }

  // Preload a single image
  preload(src) {
    if (!src || this.cache.has(src) || this.loading.has(src)) {
      return Promise.resolve();
    }

    this.loading.add(src);

    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        this.cache.set(src, true);
        this.loading.delete(src);
        resolve();
      };
      
      img.onerror = () => {
        this.loading.delete(src);
        reject(new Error(`Failed to load image: ${src}`));
      };
      
      img.src = src;
    });
  }

  // Preload multiple images with priority
  async preloadBatch(imageUrls, { maxConcurrent = 3, priority = [] } = {}) {
    const prioritySet = new Set(priority);
    
    // Sort by priority
    const sortedUrls = [...imageUrls].sort((a, b) => {
      const aPriority = prioritySet.has(a);
      const bPriority = prioritySet.has(b);
      if (aPriority && !bPriority) return -1;
      if (!aPriority && bPriority) return 1;
      return 0;
    });

    // Process in batches
    const results = [];
    for (let i = 0; i < sortedUrls.length; i += maxConcurrent) {
      const batch = sortedUrls.slice(i, i + maxConcurrent);
      const batchPromises = batch.map(url => 
        this.preload(url).catch(err => ({ error: err, url }))
      );
      
      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);
    }

    return results;
  }

  // Check if image is cached
  isCached(src) {
    return this.cache.has(src);
  }

  // Clear cache
  clearCache() {
    this.cache.clear();
  }

  // Get cache size
  getCacheSize() {
    return this.cache.size;
  }
}

// Create singleton instance
const imagePreloader = new ImagePreloader();

// Helper function to preload product images
export const preloadProductImages = async (products, options = {}) => {
  if (!products || !Array.isArray(products)) return;

  const imageUrls = products
    .map(product => {
      if (!product.image || !product.image.trim()) return null;
      
      // Handle different image path formats
      if (product.image.startsWith('http')) {
        return product.image;
      }
      
      // Construct production image URL
      const cleanPath = product.image.replace('uploads/', '');
      return `https://al-sadik.com/api/uploads/products/${cleanPath}`;
    })
    .filter(Boolean);

  return await imagePreloader.preloadBatch(imageUrls, options);
};

// Helper function to preload category images
export const preloadCategoryImages = async (categories, options = {}) => {
  if (!categories || !Array.isArray(categories)) return;

  const imageUrls = categories
    .map(category => {
      if (!category.image || !category.image.trim()) return null;
      
      if (category.image.startsWith('http')) {
        return category.image;
      }
      
      return `https://al-sadik.com/api/uploads/categories/${category.image}`;
    })
    .filter(Boolean);

  return await imagePreloader.preloadBatch(imageUrls, options);
};

export default imagePreloader;